import { useState, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  fetchSettings,
  updateBranding,
  updateContactInfo,
  addQuickLink,
  updateQuickLink,
  deleteQuickLink,
  addSupportLink,
  updateSupportLink,
  deleteSupportLink,
  addSocialLink,
  deleteSocialLink,
  updateFeatureSettings,
  updateAutoInterestSettings,
  updateAppBannerSettings,
} from '../../store/slices/settingsSlice';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Plus,
  Trash2,
  Save,
  Building2,
  Link as LinkIcon,
  Phone,
  Share2,
  Edit2,
  X,
  Check,
  Sparkles,
  Bot,
  Smartphone
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme, themes } from '../ThemeProvider';
import { SmsMethodSettings } from './SmsMethodSettings';
import { SmsCampaignSettingsAdmin } from './SmsCampaignSettingsAdmin';

export function AdminSettings() {
  const dispatch = useAppDispatch();
  const { settings, loading } = useAppSelector((state) => state.settings);
  const { theme } = useTheme();
  const currentTheme = themes[theme];

  // Branding state
  const [brandingData, setBrandingData] = useState({
    name: '',
    logo: '',
    description: '',
    tagline: '',
  });

  // Contact info state
  const [contactData, setContactData] = useState({
    address: '',
    phone: '',
    email: '',
  });

  // 🔥 NEW: Feature settings state for all tabs
  const [featureSettings, setFeatureSettings] = useState({
    discoverTabEnabled: true,
    liveTabEnabled: false,
    activityTabEnabled: true,
    messagesTabEnabled: true,
    plansTabEnabled: true,
    addonPlansEnabled: false, // NEW: Enable/Disable addon plans section
    smsInviteEnabled: true, // NEW: Enable/Disable SMS invite button in mobile sidebar
    smsInviteAutoTrigger: true, // NEW: Auto-trigger SMS invite modal on home load
  });

  // 🔥 NEW: Auto-interest settings state
  const [autoInterestSettings, setAutoInterestSettings] = useState({
    enabled: false,
    sourceType: 'dummy' as 'dummy' | 'user',
  });

  // 🔥 NEW: App download banner & popup settings state
  const [appBannerSettings, setAppBannerSettings] = useState({
    enabled: true,
    showPopup: true,
    targetDevice: 'mobile_only' as 'all' | 'mobile_only' | 'desktop_only',
    showFrequency: 'once_per_day' as 'once_per_day' | 'once_per_session' | 'always',
    text: '📱 Download our official Matrimonial App for a faster & better experience!',
    popupTitle: 'Get The Official Matrimonial App!',
    popupText: 'Enjoy instant notifications, seamless chat, and faster match discovery on our Google Play Store App.',
    buttonText: 'Download App',
    playStoreUrl: 'https://play.google.com/store/apps/details?id=com.atootbandanmatrimonialapp',
    badgeText: 'GET OUR APP',
  });

  // Quick link state
  const [newQuickLink, setNewQuickLink] = useState({ title: '', url: '' });
  const [editingQuickLink, setEditingQuickLink] = useState<string | null>(null);
  const [editQuickLinkData, setEditQuickLinkData] = useState({ title: '', url: '' });

  // Support link state
  const [newSupportLink, setNewSupportLink] = useState({ title: '', url: '' });
  const [editingSupportLink, setEditingSupportLink] = useState<string | null>(null);
  const [editSupportLinkData, setEditSupportLinkData] = useState({ title: '', url: '' });

  // Social link state
  const [newSocialLink, setNewSocialLink] = useState({ platform: 'facebook', url: '' });

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  const brandingDefaults = useMemo(() => settings?.branding, [settings]);
  const contactDefaults = useMemo(() => settings?.contactInfo, [settings]);
  const featuresDefaults = useMemo(() => settings?.features, [settings]);
  const autoInterestDefaults = useMemo(() => settings?.autoInterestSettings, [settings]);
  const appBannerDefaults = useMemo(() => settings?.appBanner, [settings]);

  useEffect(() => {
    if (brandingDefaults) {
      setBrandingData(brandingDefaults);
    }
  }, [brandingDefaults]);

  useEffect(() => {
    if (contactDefaults) {
      setContactData(contactDefaults);
    }
  }, [contactDefaults]);

  useEffect(() => {
    if (featuresDefaults) {
      setFeatureSettings(featuresDefaults);
    }
  }, [featuresDefaults]);

  useEffect(() => {
    if (autoInterestDefaults) {
      setAutoInterestSettings(autoInterestDefaults);
    }
  }, [autoInterestDefaults]);

  useEffect(() => {
    if (appBannerDefaults) {
      setAppBannerSettings({
        enabled: appBannerDefaults.enabled ?? true,
        showPopup: appBannerDefaults.showPopup ?? true,
        targetDevice: appBannerDefaults.targetDevice || 'mobile_only',
        showFrequency: appBannerDefaults.showFrequency || 'once_per_day',
        text: appBannerDefaults.text || '📱 Download our official Matrimonial App for a faster & better experience!',
        popupTitle: appBannerDefaults.popupTitle || 'Get The Official Matrimonial App!',
        popupText: appBannerDefaults.popupText || 'Enjoy instant notifications, seamless chat, and faster match discovery on our Google Play Store App.',
        buttonText: appBannerDefaults.buttonText || 'Download App',
        playStoreUrl: appBannerDefaults.playStoreUrl || 'https://play.google.com/store/apps/details?id=com.atootbandanmatrimonialapp',
        badgeText: appBannerDefaults.badgeText || 'GET OUR APP',
      });
    }
  }, [appBannerDefaults]);

  const handleUpdateBranding = async () => {
    try {
      await dispatch(updateBranding(brandingData)).unwrap();
      toast.success('Branding updated successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to update branding');
    }
  };

  const handleUpdateContact = async () => {
    try {
      await dispatch(updateContactInfo(contactData)).unwrap();
      toast.success('Contact info updated successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to update contact info');
    }
  };

  // 🔥 NEW: Handle feature settings update
  const handleUpdateFeatureSettings = async () => {
    try {
      await dispatch(updateFeatureSettings(featureSettings)).unwrap();
      toast.success('Feature settings updated successfully');
      // Refetch settings to ensure UI is in sync
      await dispatch(fetchSettings());
    } catch (error: any) {
      toast.error(error || 'Failed to update feature settings');
    }
  };

  // 🔥 NEW: Handle auto-interest settings update
  const handleUpdateAutoInterestSettings = async () => {
    try {
      await dispatch(updateAutoInterestSettings(autoInterestSettings)).unwrap();
      toast.success('Auto-interest settings updated successfully');
      await dispatch(fetchSettings());
    } catch (error: any) {
      toast.error(error || 'Failed to update auto-interest settings');
    }
  };

  // 🔥 NEW: Handle App Banner settings update
  const handleUpdateAppBannerSettings = async () => {
    try {
      await dispatch(updateAppBannerSettings(appBannerSettings)).unwrap();
      toast.success('App Download Header Banner settings updated successfully');
      await dispatch(fetchSettings());
    } catch (error: any) {
      toast.error(error || 'Failed to update App Banner settings');
    }
  };

  const handleAddQuickLink = async () => {
    if (!newQuickLink.title || !newQuickLink.url) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await dispatch(addQuickLink(newQuickLink)).unwrap();
      setNewQuickLink({ title: '', url: '' });
      toast.success('Quick link added successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to add quick link');
    }
  };

  const handleUpdateQuickLink = async (linkId: string) => {
    try {
      await dispatch(updateQuickLink({ linkId, data: editQuickLinkData })).unwrap();
      setEditingQuickLink(null);
      toast.success('Quick link updated successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to update quick link');
    }
  };

  const handleDeleteQuickLink = async (linkId: string) => {
    if (!confirm('Are you sure you want to delete this quick link?')) return;
    try {
      await dispatch(deleteQuickLink(linkId)).unwrap();
      toast.success('Quick link deleted successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to delete quick link');
    }
  };

  const handleAddSupportLink = async () => {
    if (!newSupportLink.title || !newSupportLink.url) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await dispatch(addSupportLink(newSupportLink)).unwrap();
      setNewSupportLink({ title: '', url: '' });
      toast.success('Support link added successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to add support link');
    }
  };

  const handleUpdateSupportLink = async (linkId: string) => {
    try {
      await dispatch(updateSupportLink({ linkId, data: editSupportLinkData })).unwrap();
      setEditingSupportLink(null);
      toast.success('Support link updated successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to update support link');
    }
  };

  const handleDeleteSupportLink = async (linkId: string) => {
    if (!confirm('Are you sure you want to delete this support link?')) return;
    try {
      await dispatch(deleteSupportLink(linkId)).unwrap();
      toast.success('Support link deleted successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to delete support link');
    }
  };

  const handleAddSocialLink = async () => {
    if (!newSocialLink.platform || !newSocialLink.url) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await dispatch(addSocialLink(newSocialLink)).unwrap();
      setNewSocialLink({ platform: 'facebook', url: '' });
      toast.success('Social link added successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to add social link');
    }
  };

  const handleDeleteSocialLink = async (linkId: string) => {
    if (!confirm('Are you sure you want to delete this social link?')) return;
    try {
      await dispatch(deleteSocialLink(linkId)).unwrap();
      toast.success('Social link deleted successfully');
    } catch (error: any) {
      toast.error(error || 'Failed to delete social link');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl" style={{ color: currentTheme.primary }}>
          Application Settings
        </h2>
        <p className="text-gray-600 mt-1">
          Manage branding, links, contact information, and social media
        </p>
      </div>

      {/* 🔥 NEW: App Download Header Banner Settings Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" style={{ color: currentTheme.primary }} />
            Header App Download Banner (Google Play Store)
          </CardTitle>
          <CardDescription>
            Manage top header line banner prompting users to download the app from Google Play Store
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Header Banner Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
            <div className="space-y-1">
              <Label htmlFor="app-banner-toggle" className="text-base cursor-pointer font-medium">
                Show Top Header Line Banner
              </Label>
              <p className="text-sm text-gray-600">
                When enabled, a header line will appear at the top of the website promoting your Play Store App.
              </p>
            </div>
            <Switch
              id="app-banner-toggle"
              checked={appBannerSettings.enabled}
              onCheckedChange={(checked) =>
                setAppBannerSettings({ ...appBannerSettings, enabled: checked })
              }
            />
          </div>

          {/* Popup Modal Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100">
            <div className="space-y-1">
              <Label htmlFor="app-popup-toggle" className="text-base cursor-pointer font-medium">
                Show App Download Popup Modal
              </Label>
              <p className="text-sm text-gray-600">
                When enabled, an interactive Play Store app download popup modal will prompt users on the site.
              </p>
            </div>
            <Switch
              id="app-popup-toggle"
              checked={appBannerSettings.showPopup}
              onCheckedChange={(checked) =>
                setAppBannerSettings({ ...appBannerSettings, showPopup: checked })
              }
            />
          </div>

          {/* Device & Frequency Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
            <div className="space-y-2">
              <Label htmlFor="target-device-select" className="font-medium">
                Target Devices & Resolution Filter
              </Label>
              <select
                id="target-device-select"
                value={appBannerSettings.targetDevice}
                onChange={(e) => setAppBannerSettings({ ...appBannerSettings, targetDevice: e.target.value as any })}
                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
              >
                <option value="mobile_only">📱 Mobile Devices Only (Screen ≤ 768px or Mobile UA)</option>
                <option value="all">🌐 All Devices (Desktop, Tablet & Mobile)</option>
                <option value="desktop_only">💻 Desktop Devices Only</option>
              </select>
              <p className="text-xs text-gray-500">
                Determines whether banner and popup appear on mobile resolution or all screens.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="show-frequency-select" className="font-medium">
                Display Frequency Check
              </Label>
              <select
                id="show-frequency-select"
                value={appBannerSettings.showFrequency}
                onChange={(e) => setAppBannerSettings({ ...appBannerSettings, showFrequency: e.target.value as any })}
                className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
              >
                <option value="once_per_day">📅 Once per Day (Resets every 24 Hours)</option>
                <option value="once_per_session">⏱️ Once per Session</option>
                <option value="always">⚡ Always Show Every Visit</option>
              </select>
              <p className="text-xs text-gray-500">
                Controls how frequently the popup modal is shown to returning visitors.
              </p>
            </div>
          </div>

          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="app-banner-url">Google Play Store App URL</Label>
              <Input
                id="app-banner-url"
                value={appBannerSettings.playStoreUrl}
                onChange={(e) => setAppBannerSettings({ ...appBannerSettings, playStoreUrl: e.target.value })}
                placeholder="https://play.google.com/store/apps/details?id=com.atootbandanmatrimonialapp"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="app-banner-text">Header Line Text / Prompt</Label>
              <Input
                id="app-banner-text"
                value={appBannerSettings.text}
                onChange={(e) => setAppBannerSettings({ ...appBannerSettings, text: e.target.value })}
                placeholder="📱 Download our official Matrimonial App for a faster & better experience!"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="app-popup-title">Popup Title</Label>
                <Input
                  id="app-popup-title"
                  value={appBannerSettings.popupTitle}
                  onChange={(e) => setAppBannerSettings({ ...appBannerSettings, popupTitle: e.target.value })}
                  placeholder="Get The Official Matrimonial App!"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="app-banner-button-text">Button Text</Label>
                <Input
                  id="app-banner-button-text"
                  value={appBannerSettings.buttonText}
                  onChange={(e) => setAppBannerSettings({ ...appBannerSettings, buttonText: e.target.value })}
                  placeholder="Download App"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="app-popup-text">Popup Description / Message</Label>
              <Textarea
                id="app-popup-text"
                value={appBannerSettings.popupText}
                onChange={(e) => setAppBannerSettings({ ...appBannerSettings, popupText: e.target.value })}
                placeholder="Enjoy instant notifications, seamless chat, and faster match discovery on our Google Play Store App."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="app-banner-badge-text">Badge Label</Label>
              <Input
                id="app-banner-badge-text"
                value={appBannerSettings.badgeText}
                onChange={(e) => setAppBannerSettings({ ...appBannerSettings, badgeText: e.target.value })}
                placeholder="GET OUR APP"
              />
            </div>
          </div>

          <Button
            onClick={handleUpdateAppBannerSettings}
            className="gap-2"
            style={{ backgroundColor: currentTheme.primary }}
            disabled={loading}
          >
            <Save className="h-4 w-4" />
            Save App Banner Settings
          </Button>
        </CardContent>
      </Card>

      {/* 🔥 NEW: Auto-Interest Settings Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" style={{ color: currentTheme.primary }} />
            Auto-Interest Settings
          </CardTitle>
          <CardDescription>Automatically send 1 interest daily to newly registered users</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="auto-interest-toggle" className="text-base cursor-pointer">
                Enable Auto-Interest for New Users
              </Label>
              <p className="text-sm text-gray-600">
                When enabled, new users will automatically receive 1 interest daily for their first 7 days.
              </p>
            </div>
            <Switch
              id="auto-interest-toggle"
              checked={autoInterestSettings.enabled}
              onCheckedChange={(checked) =>
                setAutoInterestSettings({ ...autoInterestSettings, enabled: checked })
              }
            />
          </div>

          {autoInterestSettings.enabled && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <Label className="text-base">Sender Profile Source</Label>
              <p className="text-sm text-gray-600 mb-2">
                Choose what kind of profile should send the automated interest.
              </p>
              <div className="flex flex-col gap-2 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="autoInterestSource"
                    value="dummy"
                    checked={autoInterestSettings.sourceType === 'dummy'}
                    onChange={() => setAutoInterestSettings({ ...autoInterestSettings, sourceType: 'dummy' })}
                    className="w-4 h-4"
                  />
                  <span>Admin Import Profiles (Dummy Profiles)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="autoInterestSource"
                    value="user"
                    checked={autoInterestSettings.sourceType === 'user'}
                    onChange={() => setAutoInterestSettings({ ...autoInterestSettings, sourceType: 'user' })}
                    className="w-4 h-4"
                  />
                  <span>Real User Profiles</span>
                </label>
              </div>
            </div>
          )}

          <Button
            onClick={handleUpdateAutoInterestSettings}
            className="gap-2"
            style={{ backgroundColor: currentTheme.primary }}
            disabled={loading}
          >
            <Save className="h-4 w-4" />
            Save Auto-Interest
          </Button>
        </CardContent>
      </Card>

      {/* 🔥 Feature Settings Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: currentTheme.primary }} />
            Feature Settings
          </CardTitle>
          <CardDescription>Enable or disable app features</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Discover Tab Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="discover-tab-toggle" className="text-base cursor-pointer">
                Discover Tab
              </Label>
              <p className="text-sm text-gray-600">
                Enable the Discover tab to show profile matches to users.
              </p>
            </div>
            <Switch
              id="discover-tab-toggle"
              checked={featureSettings.discoverTabEnabled}
              onCheckedChange={(checked) =>
                setFeatureSettings({ ...featureSettings, discoverTabEnabled: checked })
              }
            />
          </div>

          {/* Live Tab Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="live-tab-toggle" className="text-base cursor-pointer">
                Live Tab
              </Label>
              <p className="text-sm text-gray-600">
                Enable the Live tab to show currently active/online members.
              </p>
            </div>
            <Switch
              id="live-tab-toggle"
              checked={featureSettings.liveTabEnabled}
              onCheckedChange={(checked) =>
                setFeatureSettings({ ...featureSettings, liveTabEnabled: checked })
              }
            />
          </div>

          {/* Activity Tab Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="activity-tab-toggle" className="text-base cursor-pointer">
                Activity Tab
              </Label>
              <p className="text-sm text-gray-600">
                Enable the Activity tab to show interests, views, and interactions.
              </p>
            </div>
            <Switch
              id="activity-tab-toggle"
              checked={featureSettings.activityTabEnabled}
              onCheckedChange={(checked) =>
                setFeatureSettings({ ...featureSettings, activityTabEnabled: checked })
              }
            />
          </div>

          {/* Messages Tab Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="messages-tab-toggle" className="text-base cursor-pointer">
                Messages Tab
              </Label>
              <p className="text-sm text-gray-600">
                Enable the Messages tab for chat and messaging features.
              </p>
            </div>
            <Switch
              id="messages-tab-toggle"
              checked={featureSettings.messagesTabEnabled}
              onCheckedChange={(checked) =>
                setFeatureSettings({ ...featureSettings, messagesTabEnabled: checked })
              }
            />
          </div>

          {/* Plans/Premium Tab Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="plans-tab-toggle" className="text-base cursor-pointer">
                Premium Tab
              </Label>
              <p className="text-sm text-gray-600">
                Enable the Premium tab to show subscription plans and upgrades.
              </p>
            </div>
            <Switch
              id="plans-tab-toggle"
              checked={featureSettings.plansTabEnabled}
              onCheckedChange={(checked) =>
                setFeatureSettings({ ...featureSettings, plansTabEnabled: checked })
              }
            />
          </div>

          {/* Addon Plans Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="addon-plans-toggle" className="text-base cursor-pointer">
                Addon Plans
              </Label>
              <p className="text-sm text-gray-600">
                Enable the Addon Plans section to show additional subscription plans.
              </p>
            </div>
            <Switch
              id="addon-plans-toggle"
              checked={featureSettings.addonPlansEnabled}
              onCheckedChange={(checked) =>
                setFeatureSettings({ ...featureSettings, addonPlansEnabled: checked })
              }
            />
          </div>

          {/* SMS Invite Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="sms-invite-toggle" className="text-base cursor-pointer">
                SMS Invite
              </Label>
              <p className="text-sm text-gray-600">
                Enable the SMS Invite button in the mobile sidebar.
              </p>
            </div>
            <Switch
              id="sms-invite-toggle"
              checked={featureSettings.smsInviteEnabled}
              onCheckedChange={(checked) =>
                setFeatureSettings({ ...featureSettings, smsInviteEnabled: checked })
              }
            />
          </div>

          {/* SMS Invite Auto Trigger Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="space-y-1">
              <Label htmlFor="sms-invite-auto-trigger-toggle" className="text-base cursor-pointer">
                Auto Trigger SMS Invite
              </Label>
              <p className="text-sm text-gray-600">
                Automatically trigger the SMS invite modal when the home page loads.
              </p>
            </div>
            <Switch
              id="sms-invite-auto-trigger-toggle"
              checked={featureSettings.smsInviteAutoTrigger}
              onCheckedChange={(checked) =>
                setFeatureSettings({ ...featureSettings, smsInviteAutoTrigger: checked })
              }
            />
          </div>

          <Button
            onClick={handleUpdateFeatureSettings}
            className="gap-2"
            style={{ backgroundColor: currentTheme.primary }}
            disabled={loading}
          >
            <Save className="h-4 w-4" />
            Save Feature Settings
          </Button>
        </CardContent>
      </Card>

      {/* Branding Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" style={{ color: currentTheme.primary }} />
            Branding
          </CardTitle>
          <CardDescription>Update your application's branding and identity</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="brand-name">Brand Name</Label>
              <Input
                id="brand-name"
                value={brandingData.name}
                onChange={(e) => setBrandingData({ ...brandingData, name: e.target.value })}
                placeholder="SoulMate"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="brand-tagline">Tagline</Label>
              <Input
                id="brand-tagline"
                value={brandingData.tagline}
                onChange={(e) => setBrandingData({ ...brandingData, tagline: e.target.value })}
                placeholder="Find Your Perfect Match"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="brand-logo">Logo URL</Label>
            <Input
              id="brand-logo"
              value={brandingData.logo}
              onChange={(e) => setBrandingData({ ...brandingData, logo: e.target.value })}
              placeholder="https://example.com/logo.png"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="brand-description">Description</Label>
            <Textarea
              id="brand-description"
              value={brandingData.description}
              onChange={(e) => setBrandingData({ ...brandingData, description: e.target.value })}
              placeholder="India's most trusted matrimony service..."
              rows={3}
            />
          </div>
          <Button
            onClick={handleUpdateBranding}
            className="gap-2"
            style={{ backgroundColor: currentTheme.primary }}
            disabled={loading}
          >
            <Save className="h-4 w-4" />
            Save Branding
          </Button>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" style={{ color: currentTheme.primary }} />
            Contact Information
          </CardTitle>
          <CardDescription>Update your contact details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contact-address">Address</Label>
            <Input
              id="contact-address"
              value={contactData.address}
              onChange={(e) => setContactData({ ...contactData, address: e.target.value })}
              placeholder="123 Matrimony Street, Mumbai..."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact-phone">Phone</Label>
              <Input
                id="contact-phone"
                value={contactData.phone}
                onChange={(e) => setContactData({ ...contactData, phone: e.target.value })}
                placeholder="+91 1800-123-4567"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={contactData.email}
                onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                placeholder="support@soulmate.com"
              />
            </div>
          </div>
          <Button
            onClick={handleUpdateContact}
            className="gap-2"
            style={{ backgroundColor: currentTheme.primary }}
            disabled={loading}
          >
            <Save className="h-4 w-4" />
            Save Contact Info
          </Button>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" style={{ color: currentTheme.primary }} />
            Quick Links
          </CardTitle>
          <CardDescription>Manage quick navigation links in the footer</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new quick link */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg bg-gray-50">
            <Input
              placeholder="Link Title"
              value={newQuickLink.title}
              onChange={(e) => setNewQuickLink({ ...newQuickLink, title: e.target.value })}
            />
            <div className="flex gap-2">
              <Input
                placeholder="Link URL"
                value={newQuickLink.url}
                onChange={(e) => setNewQuickLink({ ...newQuickLink, url: e.target.value })}
              />
              <Button
                onClick={handleAddQuickLink}
                size="icon"
                style={{ backgroundColor: currentTheme.primary }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Existing quick links */}
          <div className="space-y-2">
            {settings?.quickLinks?.map((link) => (
              <div
                key={link._id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              >
                {editingQuickLink === link._id ? (
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <Input
                      value={editQuickLinkData.title}
                      onChange={(e) => setEditQuickLinkData({ ...editQuickLinkData, title: e.target.value })}
                    />
                    <Input
                      value={editQuickLinkData.url}
                      onChange={(e) => setEditQuickLinkData({ ...editQuickLinkData, url: e.target.value })}
                    />
                  </div>
                ) : (
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span>{link.title}</span>
                      <Badge variant={link.isActive ? 'default' : 'secondary'}>
                        {link.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <span className="text-sm text-gray-500">{link.url}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {editingQuickLink === link._id ? (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleUpdateQuickLink(link._id)}
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditingQuickLink(null)}
                      >
                        <X className="h-4 w-4 text-gray-600" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditingQuickLink(link._id);
                          setEditQuickLinkData({ title: link.title, url: link.url });
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDeleteQuickLink(link._id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Support Links */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon className="h-5 w-5" style={{ color: currentTheme.primary }} />
            Support Links
          </CardTitle>
          <CardDescription>Manage support and help links in the footer</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new support link */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg bg-gray-50">
            <Input
              placeholder="Link Title"
              value={newSupportLink.title}
              onChange={(e) => setNewSupportLink({ ...newSupportLink, title: e.target.value })}
            />
            <div className="flex gap-2">
              <Input
                placeholder="Link URL"
                value={newSupportLink.url}
                onChange={(e) => setNewSupportLink({ ...newSupportLink, url: e.target.value })}
              />
              <Button
                onClick={handleAddSupportLink}
                size="icon"
                style={{ backgroundColor: currentTheme.primary }}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Existing support links */}
          <div className="space-y-2">
            {settings?.supportLinks?.map((link) => (
              <div
                key={link._id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              >
                {editingSupportLink === link._id ? (
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <Input
                      value={editSupportLinkData.title}
                      onChange={(e) => setEditSupportLinkData({ ...editSupportLinkData, title: e.target.value })}
                    />
                    <Input
                      value={editSupportLinkData.url}
                      onChange={(e) => setEditSupportLinkData({ ...editSupportLinkData, url: e.target.value })}
                    />
                  </div>
                ) : (
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span>{link.title}</span>
                      <Badge variant={link.isActive ? 'default' : 'secondary'}>
                        {link.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <span className="text-sm text-gray-500">{link.url}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {editingSupportLink === link._id ? (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleUpdateSupportLink(link._id)}
                      >
                        <Check className="h-4 w-4 text-green-600" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditingSupportLink(null)}
                      >
                        <X className="h-4 w-4 text-gray-600" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => {
                          setEditingSupportLink(link._id);
                          setEditSupportLinkData({ title: link.title, url: link.url });
                        }}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleDeleteSupportLink(link._id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Social Media Links */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" style={{ color: currentTheme.primary }} />
            Social Media Links
          </CardTitle>
          <CardDescription>Manage your social media presence</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new social link */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-lg bg-gray-50">
            <select
              className="px-3 py-2 border rounded-md"
              value={newSocialLink.platform}
              onChange={(e) => setNewSocialLink({ ...newSocialLink, platform: e.target.value })}
            >
              <option value="facebook">Facebook</option>
              <option value="twitter">Twitter</option>
              <option value="instagram">Instagram</option>
              <option value="linkedin">LinkedIn</option>
              <option value="youtube">YouTube</option>
              <option value="pinterest">Pinterest</option>
            </select>
            <Input
              placeholder="Social Media URL"
              value={newSocialLink.url}
              onChange={(e) => setNewSocialLink({ ...newSocialLink, url: e.target.value })}
              className="md:col-span-1"
            />
            <Button
              onClick={handleAddSocialLink}
              className="gap-2"
              style={{ backgroundColor: currentTheme.primary }}
            >
              <Plus className="h-4 w-4" />
              Add Social Link
            </Button>
          </div>

          {/* Existing social links */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {settings?.socialLinks?.map((link) => (
              <div
                key={link._id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge style={{ backgroundColor: currentTheme.primary }}>
                      {link.platform}
                    </Badge>
                    <Badge variant={link.isActive ? 'default' : 'secondary'}>
                      {link.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <span className="text-sm text-gray-500 truncate block">{link.url}</span>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleDeleteSocialLink(link._id)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* SMS Method Settings */}
      <SmsMethodSettings />
    </div>
  );
}